import {useTranslation} from 'react-i18next'
import {createUseStyles} from 'react-jss'
import {Shift} from '../../redux/types'
import {formatTimeString} from '../../util/date'
import {hoursToHoursAndMinutesString} from '../../util/hourConverter'
import ListItemCard from "../ListItemCard"
import Pagination from '../Pagination'
import ShiftTypeBadge from './ShiftTypeBadge'
import ShiftStatusBadge from './ShiftStatusBadge'

interface Props {
  shiftsOnPage: Shift[]
  pageIndex: number
  pageSize: number
  totalShiftCount: number
  onShiftClick: (shift: Shift) => void
  onPageChange: (pageIndex: number) => void
}

const ShiftList = ({
                     shiftsOnPage,
                     pageIndex,
                     pageSize,
                     totalShiftCount,
                     onShiftClick,
                     onPageChange
                   }: Props) => {
  const styles = useStyles()
  const {t} = useTranslation(['custom', 'shifts']);

  const renderHeader = () => {
    return (
      <ListItemCard className={styles.listItemContainer}>
        <div className={styles.defaultCellContainer}>
          <strong>{t('date', {ns: 'common'})}</strong>
        </div>
        <div className={styles.nameCellContainer}>
          <strong>{t('employee', {ns: 'common'})}</strong>
        </div>
        <div className={`${styles.defaultCellContainer}`}>
          <strong>{t('workingHours', {ns: 'shifts'})}</strong>
        </div>
        <div className={`${styles.badgeCellContainer}`}>
          <strong>{t('type', {ns: 'common'})}</strong>
        </div>
        <div className={`${styles.badgeCellContainer}`}>
          <strong>{t('status', {ns: 'common'})}</strong>
        </div>
        <div className={`${styles.defaultCellContainer}`}>
          <strong>{t('paidWorkingHours', {ns: 'shifts'})}</strong>
        </div>
        <div className={`${styles.defaultCellContainer}`}>
          <strong>{t('grossWages', {ns: 'shifts'})}</strong>
        </div>
      </ListItemCard>
    )
  }

  const renderListItems = () => {
    return shiftsOnPage?.map((shift, index) => {
      return (
        <ListItemCard key={`shift${index}`} className={styles.listItemContainer} index={index} onClick={() => onShiftClick(shift)}>
          <div className={styles.defaultCellContainer}>
            {shift.date}
          </div>
          <div className={styles.nameCellContainer} title={shift.employee.identificationNumber}>
            {shift.employee.name}
          </div>
          <div className={`${styles.defaultCellContainer}`}>
            {shift.startTime && `${formatTimeString(shift.startTime ?? undefined)} - ${formatTimeString(shift.endTime ?? undefined)}`}
          </div>
          <div className={`${styles.badgeCellContainer}`}>
            <ShiftTypeBadge shiftType={shift.type} />        
          </div>
          <div className={`${styles.badgeCellContainer}`}>
            <ShiftStatusBadge shiftStatus={shift.status} />
          </div>
          <div className={`${styles.defaultCellContainer}`}>
            {hoursToHoursAndMinutesString(shift.hours)}
          </div>
          <div className={`${styles.defaultCellContainer}`}>
            {shift.grossValue.amount.toLocaleString('sv-SE', {minimumFractionDigits: 2})} {shift.grossValue.currencyCode.toUpperCase()}
          </div>
        </ListItemCard>
      )
    })
  }

  return (
    <div>
      <div className={styles.container}>
        {renderHeader()}
        {renderListItems()}
      </div>
      <div className={styles.pagination}>
        <Pagination
          pageIndex={pageIndex}
          itemCount={totalShiftCount}
          pageSize={pageSize}
          siblingCount={1}
          onPageChange={onPageChange}
        />
      </div>
    </div>
  )
}

export default ShiftList

const useStyles = createUseStyles({
  container: {
    width: '100%',
    overflowX: 'auto',
  },
  listItemContainer: {
    display: 'flex',
    flex: 1,
    flexDirection: 'row',
    alignContent: 'center',
    justifyContent: 'space-between'
  },
  defaultCellContainer: {
    flexShrink: 0,
    width: 150
  },
  nameCellContainer: {
    flexShrink: 0,
    width: 200
  },
  badgeCellContainer: {
    flexShrink: 0,
    flexGrow: 1,
    display: 'flex',
    width: 120,
    flexWrap: 'wrap',
  },
  buttonContainer: {
    flexShrink: 0,
    display: 'flex',
    width: 80,
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignContent: 'center',
    paddingLeft: 0
  },
  pagination: {
    marginTop: 10,
    display: 'flex',
    justifyContent: 'center'
  }
})
